import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, brl, formatApiError, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Field, inputCls, selectCls, btnPrimary, btnGhost, num, qtyFmt } from "@/components/erp/ErpUi";
import { useDefaultMarkup, suggestPrice } from "@/components/erp/DefaultMarkup";
import { useAuth } from "@/contexts/AuthContext";

const dlg = "bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-lg max-h-[90dvh] overflow-y-auto";
const EMPTY = { code: "", barcode: "", description: "", brand: "", unit: "UN", ncm: "", cost: "", price: "", stock: "", min_stock: "", location: "", category: "", tire_size: "", warranty_days: "" };

export function ProductDialog({ open, onOpenChange, product, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const defMk = useDefaultMarkup(open && !product);
  const [priceTouched, setPriceTouched] = useState(false);
  const origins = useAuth().user?.shop?.part_origins || [];
  useEffect(() => { if (open) { setF(product ? { ...EMPTY, ...product } : EMPTY); setPriceTouched(false); } }, [open, product]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const onCost = (e) => {
    const v = e.target.value;
    setF((p) => ({ ...p, cost: v, ...(!product && !priceTouched && defMk != null ? { price: suggestPrice(num(v), defMk) } : {}) }));
  };
  const cost = num(f.cost), price = num(f.price);
  const margin = price ? (((price - cost) / price) * 100).toFixed(1) : "0";
  const markup = cost ? (((price - cost) / cost) * 100).toFixed(1) : null;
  const [mkInput, setMkInput] = useState("");
  useEffect(() => { if (open) setMkInput(""); }, [open]);
  const applyMarkup = (v) => {
    setMkInput(v); setPriceTouched(true);
    if (v !== "" && cost > 0) setF((p) => ({ ...p, price: (cost * (1 + num(v) / 100)).toFixed(2) }));
  };
  const save = async (e) => {
    e.preventDefault();
    if (!f.description.trim()) { toast.error("Informe a descrição"); return; }
    setBusy(true);
    try {
      const body = { code: f.code, barcode: f.barcode, description: f.description, brand: f.brand, unit: f.unit, ncm: f.ncm,
        cost, price, min_stock: num(f.min_stock), location: f.location,
        category: f.category || null, tire_size: f.tire_size || null,
        warranty_days: f.warranty_days === "" ? null : Number(f.warranty_days) };
      const { data } = product ? await api.patch(`/erp/products/${product.id}`, body) : await api.post("/erp/products", { ...body, stock: num(f.stock) });
      toast.success("Produto salvo"); onSaved?.(data); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-dialog" className={dlg}>
        <DialogHeader><DialogTitle className="font-display uppercase">{product ? "Editar produto" : "Novo produto / peça"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label="Descrição *" className="col-span-2"><Input data-testid="product-description" value={f.description} onChange={set("description")} className={inputCls} /></Field>
          <Field label="Código interno"><Input data-testid="product-code" value={f.code} onChange={set("code")} placeholder="automático" className={inputCls} /></Field>
          <Field label="Código de barras"><Input data-testid="product-barcode" value={f.barcode} onChange={set("barcode")} inputMode="numeric" className={inputCls} /></Field>
          <Field label="Marca"><Input data-testid="product-brand" value={f.brand} onChange={set("brand")} className={inputCls} /></Field>
          <Field label="Unidade"><Input data-testid="product-unit" value={f.unit} onChange={set("unit")} placeholder="UN, L, KG, JG" className={inputCls} /></Field>
          <Field label="NCM"><Input data-testid="product-ncm" value={f.ncm} onChange={set("ncm")} inputMode="numeric" className={inputCls} /></Field>
          <Field label="Localização"><Input data-testid="product-location" value={f.location} onChange={set("location")} placeholder="Prateleira A2" className={inputCls} /></Field>
          <Field label="Custo (R$)"><Input data-testid="product-cost" value={f.cost} onChange={onCost} inputMode="decimal" className={inputCls} /></Field>
          {!!origins.length && <Field label="Origem da peça"><select data-testid="product-origin" defaultValue="" onChange={(e) => { const o = origins.find((x) => x.name === e.target.value); if (o) { setPriceTouched(true); setF((p) => ({ ...p, price: suggestPrice(num(p.cost), Number(o.markup)) })); } }} className={selectCls}><option value="">Markup por origem…</option>{origins.map((o) => <option key={o.name} value={o.name}>{o.name} ({o.markup}%)</option>)}</select></Field>}
          <Field label="Markup desejado (%)"><Input data-testid="product-markup-input" value={mkInput} onChange={(e) => applyMarkup(e.target.value)} disabled={!(cost > 0)} placeholder={cost > 0 ? "ex.: 100" : "informe o custo"} inputMode="decimal" className={inputCls} /></Field>
          <Field label="Preço de venda (R$)"><Input data-testid="product-price" value={f.price} onChange={(e) => { setMkInput(""); setPriceTouched(true); set("price")(e); }} inputMode="decimal" className={inputCls} />{!product && defMk != null && !priceTouched && <p data-testid="product-price-suggested" className="text-[11px] text-zinc-500 mt-1">Sugerido pelo markup padrão ({defMk}%)</p>}</Field>
          {!product && <Field label="Estoque inicial"><Input data-testid="product-stock" value={f.stock} onChange={set("stock")} inputMode="decimal" className={inputCls} /></Field>}
          <Field label="Estoque mínimo"><Input data-testid="product-min" value={f.min_stock} onChange={set("min_stock")} inputMode="decimal" className={inputCls} /></Field>
          <Field label="Categoria"><select data-testid="product-category" value={f.category || ""} onChange={set("category")} className={selectCls}><option value="">—</option><option value="Peças">Peças</option><option value="Pneus">Pneus</option><option value="Óleos e fluidos">Óleos e fluidos</option><option value="Filtros">Filtros</option><option value="Elétrica">Elétrica</option><option value="Freio">Freio</option><option value="Suspensão">Suspensão</option><option value="Outros">Outros</option></select></Field>
          {f.category === "Pneus" && <Field label="Medida do pneu"><Input data-testid="product-tire-size" value={f.tire_size || ""} onChange={set("tire_size")} placeholder="185/65R15" className={inputCls} /></Field>}
          <Field label="Garantia (dias)"><Input data-testid="product-warranty" type="number" min="0" value={f.warranty_days || ""} onChange={set("warranty_days")} placeholder="padrão da oficina" className={inputCls} /></Field>
          <div className="col-span-2 grid grid-cols-2 gap-2 text-sm text-zinc-400">
            <div data-testid="product-margin" className="rounded-lg border border-zinc-800 p-2">Margem <span className="text-[11px] text-zinc-500">(lucro ÷ venda)</span><div><strong className={Number(margin) < 0 ? "text-red-300" : "text-green-neon"}>{margin}%</strong></div></div>
            <div data-testid="product-markup" className="rounded-lg border border-zinc-800 p-2">Markup <span className="text-[11px] text-zinc-500">(lucro ÷ custo)</span><div><strong className={Number(markup) < 0 ? "text-red-300" : "text-amber-brand"}>{markup === null ? "—" : `${markup}%`}</strong></div></div>
          </div>
          <div className="col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="product-save" type="submit" disabled={busy} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function MoveDialog({ open, onOpenChange, product, onSaved }) {
  const [type, setType] = useState("entrada");
  const [qty, setQty] = useState("");
  const [cost, setCost] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setType("entrada"); setQty(""); setCost(""); setNote(""); } }, [open]);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(`/erp/products/${product.id}/move`, { type, qty: num(qty), note, unit_cost: cost === "" ? null : num(cost) });
      toast.success("Movimentação registrada"); onSaved?.(data); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  if (!product) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="move-dialog" className={dlg}>
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Movimentar estoque</DialogTitle>
          <DialogDescription className="text-zinc-400">{product.description} · saldo atual {qtyFmt(product.stock)} {product.unit}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label="Tipo" className="col-span-2">
            <select data-testid="move-type" value={type} onChange={(e) => setType(e.target.value)} className={selectCls}>
              <option value="entrada">Entrada</option><option value="saida">Saída</option><option value="ajuste">Ajuste (informar saldo final)</option>
            </select>
          </Field>
          <Field label={type === "ajuste" ? "Novo saldo" : "Quantidade"}><Input data-testid="move-qty" value={qty} onChange={(e) => setQty(e.target.value)} inputMode="decimal" className={inputCls} /></Field>
          {type === "entrada" && <Field label="Custo unitário (opcional)"><Input data-testid="move-cost" value={cost} onChange={(e) => setCost(e.target.value)} inputMode="decimal" className={inputCls} /></Field>}
          <Field label="Observação" className="col-span-2"><Input data-testid="move-note" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} /></Field>
          <div className="col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="move-save" type="submit" disabled={busy || qty === ""} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const MOVE_LABEL = { entrada: "Entrada", saida: "Saída", ajuste: "Ajuste", transferencia: "Transferência", estorno: "Estorno" };

export function HistoryDialog({ open, onOpenChange, product }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!open || !product) return;
    setRows(null);
    api.get("/erp/moves", { params: { product_id: product.id } }).then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e)));
  }, [open, product]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="history-dialog" className={dlg}>
        <DialogHeader><DialogTitle className="font-display uppercase">Histórico · {product?.description}</DialogTitle></DialogHeader>
        {!rows ? <Loader2 className="h-5 w-5 animate-spin text-green-brand mx-auto" /> : rows.length === 0 ? <p className="text-sm text-zinc-500">Sem movimentações.</p> : (
          <ul className="divide-y divide-zinc-800">
            {rows.map((m) => (
              <li key={m.id} data-testid={`move-row-${m.id}`} className="py-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="font-bold text-zinc-100">{MOVE_LABEL[m.type] || m.type}</span>
                  <span className={m.qty < 0 ? "text-red-300 font-bold" : "text-green-neon font-bold"}>{m.qty > 0 ? "+" : ""}{qtyFmt(m.qty)} → saldo {qtyFmt(m.balance)}</span>
                </div>
                <div className="text-[11px] text-zinc-500">{fmtDateTime(m.created_at)} · {m.user_name || "—"}{m.note ? ` · ${m.note}` : ""}</div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TransferDialog({ open, onOpenChange, products, shops, fromShopId, onSaved }) {
  const dests = (shops || []).filter((s) => s.id !== fromShopId);
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([{ product_id: "", qty: "" }]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTo(dests[0]?.id || ""); setRows([{ product_id: "", qty: "" }]); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const setRow = (i, k, v) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const save = async (e) => {
    e.preventDefault();
    const items = rows.filter((r) => r.product_id && num(r.qty) > 0).map((r) => ({ product_id: r.product_id, qty: num(r.qty) }));
    if (!to || !items.length) { toast.error("Escolha o destino e ao menos um item"); return; }
    setBusy(true);
    try { await api.post("/erp/transfers", { to_shop_id: to, items }); toast.success("Transferência feita"); onSaved?.(); onOpenChange(false); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="transfer-dialog" className={dlg}>
        <DialogHeader><DialogTitle className="font-display uppercase">Transferir para outra unidade</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Field label="Unidade de destino">
            <select data-testid="transfer-to" value={to} onChange={(e) => setTo(e.target.value)} className={selectCls}>
              {dests.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <select data-testid={`transfer-product-${i}`} value={r.product_id} onChange={(e) => setRow(i, "product_id", e.target.value)} className={`${selectCls} col-span-7`}>
                <option value="">Produto…</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.description} ({qtyFmt(p.stock)})</option>)}
              </select>
              <Input data-testid={`transfer-qty-${i}`} value={r.qty} onChange={(e) => setRow(i, "qty", e.target.value)} placeholder="Qtd" inputMode="decimal" className={`${inputCls} col-span-4`} />
              <button type="button" aria-label="Remover" onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))} className="col-span-1 text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <Button type="button" variant="outline" className={btnGhost} onClick={() => setRows((p) => [...p, { product_id: "", qty: "" }])}><Plus className="h-4 w-4 mr-1" /> Item</Button>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="transfer-save" type="submit" disabled={busy} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Transferir"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ProductPicker({ open, onOpenChange, onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/erp/products", { params: { q } }).then(({ data }) => setItems(data.items.slice(0, 50))).catch(() => setItems([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-picker" className={dlg}>
        <DialogHeader><DialogTitle className="font-display uppercase">Buscar peça no estoque</DialogTitle></DialogHeader>
        <Input data-testid="product-picker-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Descrição, código ou código de barras" className={inputCls} />
        {loading ? <Loader2 className="h-5 w-5 animate-spin text-green-brand mx-auto" /> : items.length === 0 ? <p className="text-sm text-zinc-500">Nenhum produto encontrado.</p> : (
          <ul className="divide-y divide-zinc-800 max-h-[55dvh] overflow-y-auto">
            {items.map((p) => (
              <li key={p.id}>
                <button type="button" data-testid={`pick-product-${p.id}`} onClick={() => { onPick(p); onOpenChange(false); }} className="w-full text-left py-2 px-1 hover:bg-zinc-900 rounded">
                  <div className="flex justify-between gap-2 text-sm">
                    <span className="text-zinc-100 font-medium">{p.description}</span>
                    <span className="text-green-neon font-bold whitespace-nowrap">{brl(p.price)}</span>
                  </div>
                  <div className={`text-[11px] ${p.stock <= 0 ? "text-red-300" : "text-zinc-500"}`}>{p.code} · estoque {qtyFmt(p.stock)} {p.unit}{p.stock <= 0 ? " · sem estoque (vai ficar negativo)" : ""}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
